import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace(
  '/backend/api',
  '/uploads',
);

import WithBackground from '../Common/WithBackground';

import './style.css';

const WorkerJobList = ({
  showSearch = true,
  limit = null,
  headerTitle,
  headerDescription,
  bg,
  overlay,
}) => {
  const { t, i18n } = useTranslation();

  // Generate correct job detail link based on current language
  const getJobDetailLink = (jobId) => {
    const language = i18n.language || 'pl';

    const routeMap = {
      pl: `/oferty-pracy/${jobId}`,
      rus: `/rus/vakansii/${jobId}`,
      ua: `/ua/vakansii/${jobId}`,
      en: `/en/job-offers/${jobId}`,
    };

    return routeMap[language] || `/oferty-pracy/${jobId}`;
  };

  const [jobsData, setJobsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredJobs, setFilteredJobs] = useState([]);

  // Sort offers - TOP offers always first
  const sortOffersByTop = (offers) => {
    return [...offers].sort((a, b) => (b.is_top ? 1 : 0) - (a.is_top ? 1 : 0));
  };

  // Load job offers
  useEffect(() => {
    const loadOffers = async () => {
      try {
        setLoading(true);
        const currentLang = i18n.language || 'pl';
        const limitParam = limit ? `&limit=${limit}` : '';
        const response = await fetch(
          `${API_BASE_URL}/offers.php?lang=${currentLang}${limitParam}`,
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success && data.offers) {
          const sortedOffers = sortOffersByTop(data.offers);
          setJobsData(sortedOffers);
          setFilteredJobs(sortedOffers);
        } else {
          console.warn('No job offers data received:', data);
          setJobsData([]);
          setFilteredJobs([]);
        }
      } catch (error) {
        console.error('Error loading job offers:', error);
        setJobsData([]);
        setFilteredJobs([]);
      } finally {
        setLoading(false);
      }
    };

    loadOffers();
  }, [i18n.language, limit]);

  // Filter jobs based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredJobs(jobsData);
    } else {
      const filtered = jobsData.filter(
        (job) =>
          job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredJobs(sortOffersByTop(filtered));
    }
  }, [searchTerm, jobsData]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const jobListContent = (
    <section className='worker-job-list'>
      <div className='worker-job-list__container'>
        <div className='worker-job-list__header'>
          <h2
            className='worker-job-list__title'
            data-aos='fade-up'
            data-aos-delay='100'
          >
            {headerTitle || t('jobs.title')}
          </h2>
          <p
            className='worker-job-list__description'
            data-aos='fade-up'
            data-aos-delay='200'
          >
            {headerDescription || t('jobs.description')}
          </p>
        </div>

        {showSearch && (
          <div className='worker-job-list__search'>
            <div className='search-container'>
              <input
                type='text'
                placeholder={
                  t('jobs.searchPlaceholder') || 'Szukaj ofert pracy...'
                }
                value={searchTerm}
                onChange={handleSearchChange}
                className='search-input'
              />
              <button
                className='search-button'
                aria-label={t('jobs.searchButton')}
              >
                <i className='fas fa-search'></i>
              </button>
            </div>
          </div>
        )}

        <div className='worker-job-list__content'>
          {loading ? (
            <div className='worker-job-list__loading'>
              <div className='loading-spinner'></div>
              <p>{t('common.loading') || 'Ładowanie...'}</p>
            </div>
          ) : filteredJobs.length > 0 ? (
            <div className='worker-job-list__grid'>
              {filteredJobs.map((job, index) => (
                <div
                  key={job.id}
                  className='job-card'
                  data-aos='zoom-in'
                  data-aos-delay={Math.min(index * 50, 300)}
                >
                  <div className='job-card__header'>
                    {job.image && (
                      <div className='job-card__image'>
                        <img
                          src={`${UPLOADS_BASE_URL}/${job.image}`}
                          alt={job.title}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}
                    {job.is_top && (
                      <div className='job-card__badge'>
                        <span className='top-badge'>
                          {t('workerOffer.topJob') || 'TOP'}
                        </span>
                      </div>
                    )}
                  </div>

                  <div className='job-card__content'>
                    <h3 className='job-card__title'>{job.title}</h3>

                    <div className='job-card__info'>
                      <div className='job-card__location'>
                        <i className='fas fa-map-marker-alt'></i>
                        <span>{job.location}</span>
                      </div>

                      <div className='job-card__salary'>
                        <span>{job.description}</span>
                      </div>
                    </div>
                  </div>

                  <div className='job-card__footer'>
                    <Link
                      to={getJobDetailLink(job.id)}
                      className='job-card__btn btn'
                    >
                      {t('common.readMore')}
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className='worker-job-list__empty'>
              <i className='fas fa-search'></i>
              <h3>{t('jobs.noResults') || 'Brak ofert pracy'}</h3>
              <p>
                {searchTerm
                  ? t('jobs.noSearchResults') ||
                    'Nie znaleziono ofert dla podanych kryteriów.'
                  : t('jobs.noOffers') ||
                    'Obecnie brak dostępnych ofert pracy.'}
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );

  return bg ? (
    <WithBackground bg={bg} overlay={overlay}>
      {jobListContent}
    </WithBackground>
  ) : (
    jobListContent
  );
};

WorkerJobList.propTypes = {
  showSearch: PropTypes.bool,
  limit: PropTypes.number,
  headerTitle: PropTypes.string,
  headerDescription: PropTypes.string,
  bg: PropTypes.string,
  overlay: PropTypes.oneOf(['dark', 'light', 'golden', 'none']),
};

WorkerJobList.defaultProps = {
  showSearch: true,
  limit: null,
  headerTitle: null,
  headerDescription: null,
  bg: null,
  overlay: 'dark',
};

export default WorkerJobList;
